import React from 'react';
import Link from 'next/link';
import { Typography } from '../../Typography';
import styles from './BlogCard.module.scss';
import BlogImage from '../BlogImage/BlogImage';

interface BlogPost {
  id: number | string;
  image: string;
  category: string;
  readTime: string;
  title: string;
  description: string;
  author: {
    name: string;
    avatar: string;
    date: string;
  };
  slug?: string;
  body?: string;
}

interface BlogCardProps {
  post: BlogPost;
  noBorderRadius?: boolean;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, noBorderRadius = false }) => {
  // Function to check if body has actual content (not just empty HTML tags)
  const hasActualContent = (htmlContent?: string): boolean => {
    if (!htmlContent) return false;

    // Remove all HTML tags using regex (works in both client and server)
    const textContent = htmlContent.replace(/<[^>]*>/g, '');

    // Decode HTML entities
    const decodedContent = textContent
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // Remove all whitespace and check if there's actual content
    const cleanedContent = decodedContent.trim().replace(/\s+/g, ' ');

    return cleanedContent.length > 0;
  };

  const hasBody = hasActualContent(post.body);
  const isClickable = hasBody && post.slug;

  const handleClick = () => {
    if (isClickable) {
      window.location.href = `/news/${post.slug}`;
    }
  };

  return (
    <div
      className={`${styles.card} ${
        noBorderRadius ? styles.noBorderRadius : ''
      } ${isClickable ? styles.clickable : styles.notClickable}`}
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className={`${styles.cardImage} ${noBorderRadius ? styles.noBorderRadius : ''}`}>
        <BlogImage src={post.image} alt={post.title} />
      </div>

      <div>
        {isClickable && (
          <div className={styles.cardTags}>
            <Typography
              variant="text-xs"
              as="span"
              className={styles.tag}
              style={{ color: '#D6BBFB' }}
            >
              {post.category}
            </Typography>
            <Typography
              variant="text-xs"
              as="span"
              className={styles.readTime}
              style={{ color: '#D6BBFB' }}
            >
              {post.readTime}
            </Typography>
          </div>
        )}

        <div className={styles.cardHeader}>
          <Typography variant="text-lg" as="h3" color="grayDark-50" className={styles.cardTitle}>
            {post.title}
          </Typography>
          {isClickable && (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.linkIcon}>
              <path
                d="M4 12L12 4M4 4H12V12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>

        <Typography
          variant="text-md"
          as="p"
          color="grayDark-400"
          className={styles.cardDescription}
        >
          {post.description}
        </Typography>

        <div className={styles.cardAuthor}>
          <div className={styles.authorAvatar}>
            <img
              src={post.author.avatar}
              alt={post.author.name}
              width={40}
              height={40}
              loading="lazy"
            />
          </div>
          <div className={styles.authorInfo}>
            <Typography variant="text-sm" as="span" color="primary" weight="medium">
              {post.author.name}
            </Typography>
            <Typography variant="text-sm" as="span" color="grayDark-400">
              {post.author.date}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogCard;
